'use client'

/**
 * ChatPanel — message thread + composer.
 *
 * Suggestion chips and the webhook request headers are driven by host
 * `config` (config.suggestions, config.requestHeaders) so the SAME engine
 * serves every AUMonline app without per-app edits to this file.
 */

import { useState, useRef, useEffect } from 'react'
import { useAshraryaConfig, useAshraryaTourStatus } from '../AshraryaProvider'
import { useAshraryaSession } from '../hooks/useAshraryaSession'
import { useTourEngine } from './TourEngine'
import { sendAshraryaMessage } from '../api'

// Neutral fallback chips — used only if the host passes no config.suggestions.
const DEFAULT_SUGGESTIONS = [
  { label: '🗺️ Give me a quick tour', text: 'Give me a tour' },
]

export function ChatPanel({ onMinimise }) {
  const config = useAshraryaConfig()
  const { sessionId, history, addMessage, clearSession } = useAshraryaSession({
    appNamespace: config?.appNamespace,
    userId: config?.user?.id || 'anon',
  })
  const { startTour, isActive: tourActive } = useTourEngine()
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [lastFailedText, setLastFailedText] = useState(null)
  const threadRef = useRef(null)

  const { statuses: tourStatuses, markMessageStarted } = useAshraryaTourStatus()

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight
    }
  }, [history, sending])

  const sendText = async (text) => {
    if (!text || sending || !config) return
    setError(null)
    setSending(true)
    addMessage({ role: 'user', channel: 'text', content: text })

    try {
      const payload = {
        session_id: sessionId,
        app_namespace: config.appNamespace,
        user: config.user || { id: 'anon', name: 'there', role: 'guest' },
        channel: 'text',
        input: { type: 'text', content: text, language: config.language || 'en' },
        client_meta: {
          current_path: typeof window !== 'undefined' ? window.location.pathname : '/',
          variant: config.variant || undefined,
          ui_version: config.uiVersion || 'unknown',
          viewport: typeof window !== 'undefined'
            ? { width: window.innerWidth, height: window.innerHeight }
            : undefined,
        },
      }
      const res = await sendAshraryaMessage({
        webhookUrl: config.webhookUrl,
        payload,
        headers: config.requestHeaders,
      })

      let enrichedTour = res?.tour || null
      if (enrichedTour && Array.isArray(enrichedTour.steps) && enrichedTour.steps.length > 0
          && typeof config.enrichTour === 'function') {
        try {
          const out = await config.enrichTour(enrichedTour, res?.citations || [])
          if (out && Array.isArray(out.steps) && out.steps.length > 0) {
            enrichedTour = out
          }
        } catch (err) {
          if (config?.debug) console.warn('[Ashraya] enrichTour failed:', err)
        }
      }

      addMessage({
        role: 'assistant',
        channel: res?.reply?.type || 'text',
        content: res?.reply?.content || '',
        citations: res?.citations,
        confidence: res?.confidence,
        trace_id: res?.trace_id,
        tour: enrichedTour,
      })

      if (enrichedTour && Array.isArray(enrichedTour.steps) && enrichedTour.steps.length > 0) {
        const hasPrep = enrichedTour.prep && Array.isArray(enrichedTour.prep.items) && enrichedTour.prep.items.length > 0
        if (!hasPrep) {
          setTimeout(() => {
            startTour(enrichedTour)
            if (typeof onMinimise === 'function') onMinimise()
          }, 350)
        }
      }
      setLastFailedText(null)
    } catch (e) {
      if (config?.debug) console.error('[Ashraya] send failed:', e)
      setError(e?.message || 'Could not reach Ashraya right now.')
      setLastFailedText(text)
    } finally {
      setSending(false)
    }
  }

  const handleSend = () => {
    const text = input.trim()
    if (!text) return
    setInput('')
    sendText(text)
  }

  const handleRetry = () => {
    if (lastFailedText) sendText(lastFailedText)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (!config) return null
  const primary = config.theme?.primary || '#f97316'
  const suggestions = (Array.isArray(config.suggestions) && config.suggestions.length > 0)
    ? config.suggestions
    : DEFAULT_SUGGESTIONS

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div
        className="px-4 py-3 text-white flex items-center justify-between"
        style={{ backgroundColor: primary }}
      >
        <div className="min-w-0">
          <div className="font-semibold truncate">{config.appName} Help</div>
          <div className="text-xs font-normal opacity-90">Ashraya • powered by AUMonline</div>
        </div>
        <button
          onClick={() => {
            if (history.length === 0) return
            if (typeof window !== 'undefined' && window.confirm('Clear this conversation?')) {
              clearSession()
            }
          }}
          title="Clear conversation"
          className="text-white/80 hover:text-white text-sm shrink-0 ml-2"
          aria-label="Clear conversation"
        >
          ↻
        </button>
      </div>

      {/* Thread */}
      <div ref={threadRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {history.length === 0 && !sending && (
          <div className="text-center text-slate-500 text-sm pt-2">
            <div className="text-4xl mb-2">👋</div>
            <p className="mb-4">Hi {config.user?.name || 'there'}, ask me anything about <b>{config.appName}</b>.</p>
            <div className="flex flex-col gap-2 text-left">
              {suggestions.map((s, i) => (
                <SuggestionChip key={i} primary={primary} tourId={s.tourId} onClick={() => sendText(s.text)}>
                  {s.label}
                </SuggestionChip>
              ))}
            </div>
          </div>
        )}

        {history.map((m) => (
          <MessageBubble
            key={m.id}
            msg={m}
            primary={primary}
            tourStatusForMsg={tourStatuses[m.id] || null}
            onStartTour={() => {
              if (m.tour && Array.isArray(m.tour.steps) && m.tour.steps.length > 0) {
                markMessageStarted(m.id)
                startTour(m.tour)
                if (typeof onMinimise === 'function') onMinimise()
              }
            }}
            tourActive={tourActive}
          />
        ))}

        {/* Quick guides stay reachable after a conversation starts — guided
            tours end by spotlighting one of these cards (relay handoff), so
            they must exist even when history is non-empty. */}
        {history.length > 0 && !sending && (
          <div className="pt-1" data-testid="ashraya-quick-guides">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Quick guides</div>
            <div className="flex flex-col gap-2 text-left">
              {suggestions.map((s, i) => (
                <SuggestionChip key={i} primary={primary} tourId={s.tourId} onClick={() => sendText(s.text)}>
                  {s.label}
                </SuggestionChip>
              ))}
            </div>
          </div>
        )}

        {sending && (
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <TypingDots />
            <span>Thinking…</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            <div className="mb-1">{error}</div>
            {lastFailedText && (
              <button onClick={handleRetry} className="underline text-red-800 text-xs">
                Retry
              </button>
            )}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-slate-200 p-3 bg-white">
        <div className="flex gap-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={tourActive ? 'Tour in progress…' : `Ask about ${config.appName}…`}
            disabled={sending || tourActive}
            rows={1}
            data-testid="ashraya-input"
            className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-500 disabled:bg-slate-100 disabled:text-slate-400"
            style={{ minHeight: 38, maxHeight: 120 }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending || tourActive}
            data-testid="ashraya-send"
            className="rounded-lg px-3 py-2 text-white text-sm font-medium disabled:opacity-50 transition-opacity"
            style={{ backgroundColor: primary }}
          >
            Send
          </button>
        </div>
        {tourActive && (
          <div className="text-xs text-slate-500 mt-2 italic">
            Tour in progress — follow the highlighted steps on the page.
          </div>
        )}
      </div>
    </div>
  )
}

function MessageBubble({ msg, primary, onStartTour, tourActive, tourStatusForMsg }) {
  const isUser = msg.role === 'user'
  const hasPrep = !!(msg.tour && msg.tour.prep && Array.isArray(msg.tour.prep.items) && msg.tour.prep.items.length > 0)
  const isConsumed = tourStatusForMsg === 'started' || tourStatusForMsg === 'completed' || tourStatusForMsg === 'abandoned'
  const isCompleted = tourStatusForMsg === 'completed'
  const isAbandoned = tourStatusForMsg === 'abandoned'

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
          isUser ? 'text-white' : 'bg-white border border-slate-200 text-slate-800'
        }`}
        style={isUser ? { backgroundColor: primary } : undefined}
      >
        <div
          className="leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
        />

        {!isUser && hasPrep && !isConsumed && (
          <div className="mt-3 border border-slate-200 rounded-xl p-3 bg-slate-50">
            <div className="text-xs font-semibold text-slate-700 mb-2">
              {msg.tour.prep.title || 'Before we begin, have these handy:'}
            </div>
            <ul className="space-y-1.5 mb-3">
              {msg.tour.prep.items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs">
                  <span
                    className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.required ? primary : '#94a3b8' }}
                    aria-hidden="true"
                  />
                  <span className="leading-snug">
                    <span className={item.required ? 'font-medium text-slate-800' : 'text-slate-700'}>
                      {item.label}
                    </span>
                    {item.required && (
                      <span className="text-[10px] uppercase tracking-wide ml-1.5" style={{ color: primary }}>
                        Required
                      </span>
                    )}
                    {item.note && (
                      <span className="text-slate-500 ml-1">— {item.note}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => {
                if (typeof onStartTour === 'function') onStartTour()
              }}
              disabled={tourActive}
              className="w-full rounded-lg px-3 py-2 text-white text-sm font-medium disabled:opacity-50 transition-opacity hover:opacity-90"
              style={{ backgroundColor: primary }}
            >
              I&apos;m ready, let&apos;s go →
            </button>
            <div className="text-[10px] text-slate-400 mt-1.5 text-center italic">
              Don&apos;t have everything? You can save with what you have and edit later.
            </div>
          </div>
        )}

        {!isUser && msg.tour && (!hasPrep || isConsumed) && (
          <div className="mt-2 text-xs italic opacity-90">
            {isCompleted && '✅ '}
            {isAbandoned && '⏸️ '}
            {!isCompleted && !isAbandoned && '🎯 '}
            Tour: <b>{msg.tour.title || msg.tour.id}</b>
            {isCompleted && <span className="ml-1 not-italic">— done!</span>}
            {isAbandoned && <span className="ml-1 not-italic text-slate-500">— skipped</span>}
          </div>
        )}

        {Array.isArray(msg.citations) && msg.citations.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {msg.citations.map((c, i) => (
              <span
                key={i}
                className="text-[10px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5"
              >
                📖 {c.title}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function SuggestionChip({ children, onClick, primary, tourId }) {
  return (
    <button
      onClick={onClick}
      data-testid="ashraya-suggestion"
      data-tour-id={tourId || undefined}
      className="text-left text-sm rounded-lg bg-white px-3 py-2 hover:shadow-sm transition"
      style={{ border: `1px solid ${primary}33` }}
    >
      {children}
    </button>
  )
}

function TypingDots() {
  return (
    <span className="inline-flex gap-1">
      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </span>
  )
}

// Minimal markdown: bold, italic, line breaks. HTML-escapes input first.
function renderMarkdown(text = '') {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>')
}
